import { createClient, Provider, cacheExchange, fetchExchange } from 'urql';
import PropTypes from 'prop-types';
import { action } from '@storybook/addon-actions';
import { ErrorBoundary } from 'react-error-boundary';
import { updateMockSettings } from '../mocks/handlers';
import HealthyDealsDataContextProvider from '../components/HealthyDealsDataContextProvider/HealthyDealsData.context';
import {
	componentMainTitle,
	mockHealthyDealsProps,
} from '../utils/miscConstants';

/**
 * Live API testing controls for components that use the GraphQL API.
 */
export const storyArgs = {
	apiEnvironment: {
		control: 'select',
		options: ['dev', 'tst', 'stg', 'prod'],
	},
	disableMocks: {
		control: 'boolean',
	},
	ssId: {
		control: 'text',
	},
};

export const mockHealthyDealsDataContextStoryProps = {
	setMainTitleTo: componentMainTitle,
	componentRootPath: '/home',
	redirectToResourceLibrary: action(
		'button-click.\n Consumer would pass-in `redirectToResourceLibrary` function and handles this functionality\n',
	),
	redirectToLogin: action(
		'button-click. \n Consumer would pass-in `redirectToLogin` function and handles this functionality\n',
	),
	redirectToNetworkSearch: action(
		'button-click.\n Consumer would pass-in `redirectToNetworkSearch` function and handles this functionality\n',
	),
	updatePageTitleAndDescription: action(
		'on-load.\n Consumer would pass-in `updatePageTitleAndDescription` function and handles this functionality.\n They would set page title and description on their page with these arguments.\n',
	),
};

export const defaultArgs = {
	apiEnvironment: 'dev',
	disableMocks: false,
	ssId: '',
	hpCode: '',
	...mockHealthyDealsProps,
	...mockHealthyDealsDataContextStoryProps,
};

/** Environments the component can live on */
export const environments = {
	dev: 'dev',
	tst: 'tst',
	stg: 'stg',
};

/**
 * @param {string} apiEnvironment - current environment
 * @returns an API url for respecitve environment
 */
export const getActualGraphQLEndpoint = apiEnvironment =>
	apiEnvironment !== 'prod'
		? `https://${environments[apiEnvironment]}.healthydeals.api.ashcompanies.com/graphql`
		: 'https://healthydeals.api.ashcompanies.com/graphql';

/**
 *
 * @param {string} apiEnvironment - prefix denoting the current environment
 * @param {string} ssId - ssId code for the currently logged in user
 * @param {string} hpCode - hpCode for the current client
 * @returns an URQL client configured with above
 */
export const getClient = (apiEnvironment, ssId, hpCode) => {
	const headers = {};
	if (ssId) {
		headers['x-ss-id'] = ssId;
	}
	// only send hpcode if it's provided
	if (hpCode) {
		headers.hpCode = hpCode;
	}
	return createClient({
		url: getActualGraphQLEndpoint(apiEnvironment),
		maskTypename: true,
		fetchOptions: {
			headers,
		},
		exchanges: [cacheExchange, fetchExchange],
	});
};
/**
 * Wrapper component for Storybook that sets the `urql` client according to the
 * passed parameters.
 */
export const StorybookClient = ({
	graphQlEndpoint = 'https://dev.healthydeals.api.ashcompanies.com/graphql',
	disableMocks = false,
	ssId,
	hpCode = null,
	children,
}) => {
	if (disableMocks) {
		import('../mocks/browser').then(({ worker }) => worker.stop());
	}
	// Create a new GQL client every story transition so we clear the cache
	const headers = {};
	if (ssId) {
		headers['x-ss-id'] = ssId;
	}
	if (hpCode) {
		headers.hpCode = hpCode;
	}
	const urqlClient = createClient({
		url: graphQlEndpoint,
		fetchOptions: {
			headers,
		},
		// Ensure that we always bypass the cache in tests
		requestPolicy: 'network-only',
		exchanges: [cacheExchange, fetchExchange],
	});
	return <Provider value={urqlClient}>{children}</Provider>;
};
StorybookClient.propTypes = {
	graphQlEndpoint: PropTypes.string,
	disableMocks: PropTypes.bool,
	ssId: PropTypes.string.isRequired,
	hpCode: PropTypes.string,
	children: PropTypes.node.isRequired,
};

/**
 * This array of decorators should be used in all stories. They can be added to a story like this
 * if it has no decorators of its own:
 * ```javascript
 * title: 'Pages/Dashboard',
 * decorators: globalDecorators,
 * ```
 * Or like this if it has decorators:
 * ```javascript
 * title: 'Pages/My Account',
 * decorators: [
 *   () => <RouterProvider router={dynamicRouter} />,
 *   ...globalDecorators,
 * ],
 * ```
 */
export const globalDecorators = [
	// This decorator updates our mock settings. The story must add a `mockSettings` property to its
	// `parameters` object.
	(Story, context) => {
		updateMockSettings(context.parameters.mockSettings || {});
		const clientArgs = { ...storyArgs, ...context.args };
		return (
			<StorybookClient
				graphQlEndpoint={getActualGraphQLEndpoint(clientArgs.apiEnvironment)}
				disableMocks={clientArgs.disableMocks}
				ssId={clientArgs.ssId}
				hpCode={clientArgs.hpCode}
			>
				<HealthyDealsDataContextProvider
					{...mockHealthyDealsDataContextStoryProps}
					isUserAuthenticated={!!clientArgs.ssId}
				>
					<Story />
				</HealthyDealsDataContextProvider>
			</StorybookClient>
		);
	},
	Story => (
		<ErrorBoundary
			key="error-boundary-1"
			FallbackComponent={() => <div>App will handle the error case</div>}
		>
			<Story />
		</ErrorBoundary>
	),
];

/**
 * Wrapper component that sets the urql client according to passed parameters
 * This is a higher order component takes in a UI component children, and decorates it with urql client to make graphQL calls
 */
export const UrqlRender = ({
	apiEnvironment,
	ssId = null,
	hpCode = null,
	children,
}) => (
	<Provider value={getClient(apiEnvironment, ssId, hpCode)}>
		{children}
	</Provider>
);

UrqlRender.propTypes = {
	apiEnvironment: PropTypes.string.isRequired,
	ssId: PropTypes.string,
	hpCode: PropTypes.string,
	children: PropTypes.node.isRequired,
};
